import { Bars3Icon } from "@heroicons/react/24/outline";
import { usePage } from "@inertiajs/react";
import type { ReactNode } from "react";
import { useState } from "react";
import FlashMessage from "@/components/FlashMessage";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ScoutExternalModeOverlay from "@/components/ScoutExternalModeOverlay";
import Sidebar from "@/components/Sidebar";
import type { SharedProps } from "@/types";

type LayoutProps = {
  children: ReactNode;
  title: string;
  hideNav?: boolean;
  header?: ReactNode;
};

export default function Layout({ children, title, hideNav = false, header }: LayoutProps) {
  const { flash, impersonation } = usePage<SharedProps>().props;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-25">
      <title>{`${title} — ScoutUX`}</title>

      {hideNav ? null : <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        {impersonation ? <ImpersonationBanner name={impersonation.name} email={impersonation.email} /> : null}

        {hideNav ? null : (
          <div className="relative z-50 border-b border-gray-200 bg-gray-25 md:hidden">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  className="flex shrink-0 items-center text-gray-700"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>

                <p className="min-w-0 truncate text-right font-semibold">{title}</p>
              </div>
            </div>
          </div>
        )}

        {header}

        <main className="flex-1 overflow-y-auto" scroll-region="">
          <div id="top" />
          <div className="container mx-auto p-4 md:p-6">
            <FlashMessage flash={flash} />
            {children}
          </div>
        </main>

        <div
          id="layout-main-overlay"
          className={`pointer-events-none absolute inset-0 z-40 ${hideNav ? "" : "max-md:top-14"}`}
        />
      </div>

      <ScoutExternalModeOverlay />
    </div>
  );
}
